<template>
    <v-expansion-panel>
        <v-expansion-panel-header expand-icon="mdi-bell-outline">
            <template v-slot:default="{ open }">
                Push Notifications
                <span v-if="open" key="0" class="ml-auto header-helper-text pr-2 text-right">(click to close)</span>
                <span v-else key="1" class="ml-auto header-helper-text pr-2 text-right">(click to expand)</span>
            </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
            <v-card outlined class="push-notifications-card">
                <v-card-text class="push-notifications-content">
                    <div class="push-notifications-summary">
                        <v-avatar size="40" class="push-notifications-icon">
                            <v-icon color="primary">mdi-bell-ring-outline</v-icon>
                        </v-avatar>
                        <div class="push-notifications-copy">
                            <div class="push-notifications-title">Alarm alerts</div>
                            <div class="push-notifications-caption">
                                Receive boat alerts when MarineOne is closed.
                            </div>
                        </div>
                        <v-chip
                            x-small
                            label
                            :color="enabled ? 'success' : 'grey lighten-2'"
                            :text-color="enabled ? 'white' : 'grey darken-2'"
                        >
                            {{ enabled ? "ON" : "OFF" }}
                        </v-chip>
                    </div>

                    <v-divider class="push-notifications-divider"></v-divider>

                    <div class="push-notifications-platform-note">
                        <v-icon small color="primary">mdi-apple</v-icon>
                        <span>
                            iPhone/iPad: requires iOS 16.4 or later. Add MarineOne to your Home Screen and open it there first.
                        </span>
                    </div>

                    <v-alert v-if="errorMessage" type="error" dense text class="push-notifications-message">
                        {{ errorMessage }}
                    </v-alert>
                    <v-alert v-if="successMessage" type="success" dense text class="push-notifications-message">
                        {{ successMessage }}
                    </v-alert>
                    <v-alert v-if="!supported" type="info" dense text class="push-notifications-message">
                        Push notifications are not available in this browser.
                    </v-alert>

                    <v-btn
                        v-if="supported && !enabled"
                        color="primary"
                        block
                        :loading="busy"
                        :disabled="busy || !serverConfigured"
                        class="push-notifications-action"
                        @click="enable"
                    >
                        <v-icon left small>mdi-bell-plus-outline</v-icon>
                        Enable on this device
                    </v-btn>
                    <v-btn
                        v-else-if="supported"
                        color="secondary"
                        block
                        :loading="busy"
                        :disabled="busy"
                        class="push-notifications-action"
                        @click="disable"
                    >
                        <v-icon left small>mdi-bell-off-outline</v-icon>
                        Disable on this device
                    </v-btn>
                </v-card-text>
            </v-card>
        </v-expansion-panel-content>
    </v-expansion-panel>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
    disablePushNotifications,
    enablePushNotifications,
    getPushPublicKey,
    syncDevicePushSubscription,
} from "@/services/pushNotifications";

export default defineComponent({
    name: "PushNotifications",
    data() {
        return {
            busy: false,
            enabled: false,
            supported: false,
            serverConfigured: false,
            publicKey: "",
            errorMessage: "",
            successMessage: "",
        };
    },
    async mounted() {
        this.supported =
            "serviceWorker" in navigator &&
            "PushManager" in window &&
            "Notification" in window;
        if (!this.supported) {
            return;
        }

        try {
            this.publicKey = await getPushPublicKey();
            this.serverConfigured = true;
        } catch (error) {
            this.errorMessage = "Push notifications are not configured on the server yet.";
            console.error("Failed to load push notification configuration.", error);
        }

        try {
            const subscription = await syncDevicePushSubscription();
            this.enabled = !!subscription && Notification.permission === "granted";
        } catch (error) {
            this.errorMessage = "Could not sync this device's notification subscription.";
            console.error("Failed to sync push subscription.", error);
        }
    },
    methods: {
        async enable() {
            this.busy = true;
            this.errorMessage = "";
            this.successMessage = "";
            try {
                await enablePushNotifications(this.publicKey);
                this.enabled = true;
                this.successMessage = "Push notifications are enabled on this device.";
            } catch (error) {
                this.errorMessage = error instanceof Error
                    ? error.message
                    : "Could not enable push notifications.";
                console.error("Failed to enable push notifications.", error);
            } finally {
                this.busy = false;
            }
        },
        async disable() {
            this.busy = true;
            this.errorMessage = "";
            this.successMessage = "";
            try {
                await disablePushNotifications();
                this.enabled = false;
                this.successMessage = "Push notifications are disabled on this device.";
            } catch (error) {
                this.errorMessage = error instanceof Error
                    ? error.message
                    : "Could not disable push notifications.";
                console.error("Failed to disable push notifications.", error);
            } finally {
                this.busy = false;
            }
        },
    },
});
</script>

<style scoped>
.push-notifications-card {
    border-radius: 8px;
}

.push-notifications-content {
    padding: 12px;
}

.push-notifications-summary {
    display: flex;
    align-items: center;
    gap: 10px;
}

.push-notifications-icon {
    flex: 0 0 auto;
    background: var(--app-color-primary-soft);
}

.push-notifications-copy {
    min-width: 0;
    flex: 1;
}

.push-notifications-title {
    color: rgba(0, 0, 0, 0.87);
    font-size: var(--app-font-body);
    font-weight: 600;
    line-height: 1.25;
}

.push-notifications-caption {
    margin-top: 3px;
    color: rgba(0, 0, 0, 0.6);
    font-size: var(--app-font-caption);
    line-height: 1.35;
}

.push-notifications-divider {
    margin: 12px 0;
}

.push-notifications-platform-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px;
    border-radius: 6px;
    background: var(--app-color-primary-soft);
    color: rgba(0, 0, 0, 0.72);
    font-size: var(--app-font-caption);
    line-height: 1.4;
}

.push-notifications-platform-note .v-icon {
    flex: 0 0 auto;
}

.push-notifications-message {
    margin-top: 10px;
    margin-bottom: 0;
}

.push-notifications-action {
    min-height: 38px;
    margin-top: 12px;
    font-size: var(--app-font-body);
    letter-spacing: normal;
    text-transform: none;
}
</style>
